/* eslint-disable prefer-arrow-callback */
import { useEffect, useRef, useState } from "react";

import { PDF_CHART_PAINT_DELAY_MS, PDF_PRINT_SAFETY_TIMEOUT_MS } from "consts";

type PrintWhenReadyArgs = {
  /** Nothing is printed (and no safety timer runs) without content. */
  hasContent: boolean;
  /** Data the print waits for; the safety timeout prints without it. */
  isDataReady: boolean;
  /** Runs right before the print dialog opens. */
  onBeforePrint?: () => void;
  onAfterPrint: () => void;
};

/** Opens the print dialog once, after the data is in (or the safety timeout passes) and the charts have painted. */
export const usePrintWhenReady = ({ hasContent, isDataReady, onBeforePrint, onAfterPrint }: PrintWhenReadyArgs): { printReady: boolean } => {
  const [printTimedOut, setPrintTimedOut] = useState(false);
  const hasPrintedRef = useRef(false);
  const onBeforePrintRef = useRef(onBeforePrint);
  onBeforePrintRef.current = onBeforePrint;
  const onAfterPrintRef = useRef(onAfterPrint);
  onAfterPrintRef.current = onAfterPrint;

  useEffect(
    function armPrintSafetyTimeout() {
      if (!hasContent) return undefined;
      const id = setTimeout(() => setPrintTimedOut(true), PDF_PRINT_SAFETY_TIMEOUT_MS);
      return () => clearTimeout(id);
    },
    [hasContent]
  );

  const printReady = hasContent && (isDataReady || printTimedOut);

  // Prints once, after React has painted the data and the charts have measured.
  useEffect(
    function printOnceAfterPaint() {
      if (!printReady || hasPrintedRef.current) return undefined;
      let afterPrintHandler: (() => void) | null = null;
      const id = setTimeout(() => {
        hasPrintedRef.current = true;
        afterPrintHandler = () => onAfterPrintRef.current();
        window.addEventListener("afterprint", afterPrintHandler, { once: true });
        onBeforePrintRef.current?.();
        window.print();
      }, PDF_CHART_PAINT_DELAY_MS);
      return () => {
        clearTimeout(id);
        if (afterPrintHandler) {
          window.removeEventListener("afterprint", afterPrintHandler);
        }
      };
    },
    [printReady]
  );

  return { printReady };
};
